/** Shared dimensions and wire format; browsers never simulate domino physics. */
export const DOMINO_BUILDING_TYPE = 6251;
export const DOMINO_LIMIT = 128;
export const DOMINO_HALF = { x: 0.18, y: 0.4, z: 0.065 };
export const DOMINO_FRAME_MS = 50;
export const DOMINO_MAX_FRAMES = 901;
export type DominoOptions = {
  tilt: number;
  a: number;
  b: number;
  /** Sub-cell position; tiles are only used for chunk indexing. */
  offsetX?: number;
  offsetZ?: number;
  yaw?: number;
};
export type DominoContact = {
  from: { x: number; y: number; z: number };
  to: { x: number; y: number; z: number };
  yaw: number;
  tilt: number;
  avatar?: boolean;
};
export type DominoAction = {
  action: 'contact' | 'walk' | 'reset';
  buildingId: number;
  contact?: DominoContact;
};
/** A complete double-six set, without replacement until the next set. */
export class DominoBag {
  private remaining: { a: number; b: number }[] = [];
  next(): { a: number; b: number } {
    if (this.remaining.length === 0)
      for (let a = 0; a <= 6; a++) for (let b = a; b <= 6; b++) this.remaining.push({ a, b });
    const [piece] = this.remaining.splice(Math.floor(Math.random() * this.remaining.length), 1);
    if (piece === undefined) throw new Error('Empty domino set');
    return piece;
  }
}
export const DEFAULT_DOMINO: DominoOptions = { tilt: 0, a: 6, b: 6 };
export function dominoOptions(parameters: string): DominoOptions {
  try {
    const value: unknown = JSON.parse(parameters);
    if (validDominoOptions(value)) return value;
  } catch {
    /* Older placements use the upright double-six default. */
  }
  return { ...DEFAULT_DOMINO };
}
export function validDominoOptions(value: unknown): value is DominoOptions {
  if (value === null || typeof value !== 'object') return false;
  const { tilt, a, b } = value as Record<string, unknown>;
  return (
    typeof tilt === 'number' &&
    Number.isInteger(tilt) &&
    tilt >= 0 &&
    tilt <= 90 &&
    typeof a === 'number' &&
    Number.isInteger(a) &&
    a >= 0 &&
    a <= 6 &&
    typeof b === 'number' &&
    Number.isInteger(b) &&
    b >= 0 &&
    b <= 6 &&
    ['offsetX', 'offsetZ', 'yaw'].every((key) => {
      const n = (value as Record<string, unknown>)[key];
      return (
        n === undefined ||
        (typeof n === 'number' &&
          Number.isFinite(n) &&
          (key === 'yaw' ? n >= 0 && n < Math.PI * 2 : n >= -0.25 && n <= 0.25))
      );
    })
  );
}
export type DominoPlacement = {
  id: number;
  stamp: string;
  x: number;
  y: number;
  z: number;
  yaw: number;
  tilt: number;
};
export type DominoConfiguration = {
  bricks: DominoPlacement[];
  terrain: { width: number; heights: number[] };
  colliders: { x: number; y: number; z: number; hx: number; hy: number; hz: number }[];
  pushId: number;
  contact?: DominoContact;
  initial?: number[];
};
export type DominoRecording = {
  frameCount: number;
  frames: string;
  limited: boolean;
};
export type DominoReplay = DominoRecording & {
  revision: string;
  elapsedMilliseconds: number;
  bricks: { id: number; stamp: string }[];
};
export type DominoResponse = { revision: string; replay?: DominoReplay | null };

export function dominoStamp(building: {
  id: number;
  tileX: number;
  tileZ: number;
  rotation: number;
  parameters: string;
  placedAt?: number;
  placementY?: number;
}): string {
  // A placement identity prevents a delayed recording from moving an edited/replaced brick.
  return JSON.stringify([
    building.id,
    building.tileX,
    building.tileZ,
    building.rotation,
    building.placedAt ?? 0,
    building.placementY ?? null,
    building.parameters,
  ]);
}

export type DominoPose = [number, number, number, number, number, number, number];
export function dominoInitialPose(brick: DominoPlacement): DominoPose {
  const tilt = (-brick.tilt * Math.PI) / 180;
  const sy = Math.sin(brick.yaw / 2),
    cy = Math.cos(brick.yaw / 2);
  const sx = Math.sin(tilt / 2),
    cx = Math.cos(tilt / 2);
  return [
    brick.x,
    brick.y + DOMINO_HALF.y * Math.abs(Math.cos(tilt)) + DOMINO_HALF.z * Math.abs(Math.sin(tilt)),
    brick.z,
    cy * sx,
    sy * cx,
    -sy * sx,
    cy * cx,
  ];
}

const decodedRecordings = new WeakMap<DominoReplay, Float32Array>();
export function decodeDominoFrames(replay: DominoReplay): Float32Array {
  const cached = decodedRecordings.get(replay);
  if (cached !== undefined) return cached;
  if (
    !Number.isInteger(replay.frameCount) ||
    replay.frameCount < 1 ||
    replay.frameCount > DOMINO_MAX_FRAMES ||
    replay.bricks.length < 1 ||
    replay.bricks.length > DOMINO_LIMIT ||
    replay.frames.length > 4_400_000
  )
    throw new Error('Invalid domino recording');
  const raw = atob(replay.frames);
  const expected = replay.frameCount * replay.bricks.length * 7;
  if (raw.length !== expected * 4) throw new Error('Invalid domino recording length');
  const bytes = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
  const view = new DataView(bytes.buffer);
  const frames = new Float32Array(expected);
  for (let i = 0; i < expected; i++) {
    const value = view.getFloat32(i * 4, true);
    if (!Number.isFinite(value)) throw new Error('Invalid domino pose');
    frames[i] = value;
  }
  decodedRecordings.set(replay, frames);
  return frames;
}
